import { defineStore } from 'pinia'
import { getRecentActivity } from '@/services/api'
import { TeamSide } from '@/types/promotionalEvents'

export const ACTIVITY_FEED_OPEN_STORAGE_KEY = 'Skinwhales:activity_feed_open'

export interface ActivityReward {
    balance?: number
    tokens?: number
    whalePoints?: number
}

export interface ActivityUser {
    id: string
    name: string
    avatar: string
    isVip?: boolean
    eventTeam?: {
        id: TeamSide
        name: string
    } | null
}

export interface ActivityGame {
    slug: string
}

export interface TargetItem {
    id: string
    name: string
    price: number
    image: string
}

export interface ArcadeWinActivity {
    type: 'ARCADE_WIN'
    user: ActivityUser
    game: ActivityGame
    reward: ActivityReward
    timestamp: string
}

export interface NewUserActivity {
    type: 'NEW_USER'
    user: ActivityUser
    timestamp: string
}

export interface UpgradeActivity {
    type: 'UPGRADE_5X'
    user: ActivityUser
    targetItem: TargetItem
    multiplier: number
    won: boolean
    timestamp: string
}

export interface LevelUpActivity {
    type: 'LEVEL_UP'
    user: ActivityUser
    level: number
    timestamp: string
}

export interface GiveawayWinActivity {
    type: 'GIVEAWAY_WIN'
    user: ActivityUser
    item: TargetItem
    roundId: string
    giveawayTypeId: string
    timestamp: string
}

export interface CaseOpen2xActivity {
    type: 'CASE_OPEN_2X'
    user: ActivityUser
    case: {
        id: string
        name: string
        image: string
        itemImage: string
        isVip: boolean
        isDailyCase: boolean
    }
    item: TargetItem
    profitMultiplier: number
    timestamp: string
}

export interface ArenaWinActivity {
    type: 'ARENA_WIN'
    user: ActivityUser
    room: {
        id: string
        shortId: string
        cost: number
    }
    wonAmount: number
    timestamp: string
}

export interface SkinClashWinActivity {
    type: 'SKIN_CLASH_WIN'
    user: ActivityUser
    round: {
        id: string
        roundNumber: number
        totalValue: number
    }
    wonAmount: number
    feeAmount: number
    itemCount: number
    timestamp: string
}


export interface SafeboxWinActivity {
    type: 'SAFEBOX_WIN'
    user: ActivityUser
    roundId: number
    winningCode: number
    jackpot: number
    wonAmount: number
    timestamp: string
}

export interface TeamMilestoneActivity {
    type: 'TEAM_MILESTONE'
    event: {
        id: string
        name: string
    }
    team: {
        id: TeamSide
        name: string
    }
    milestoneValue: number
    rewardTokens: number
    timestamp: string
}

export type ActivityItem =
    | ArcadeWinActivity
    | NewUserActivity
    | UpgradeActivity
    | LevelUpActivity
    | GiveawayWinActivity
    | CaseOpen2xActivity
    | TeamMilestoneActivity
    | ArenaWinActivity
    | SkinClashWinActivity
    | SafeboxWinActivity
    | WeeklyLeaderboardRewardActivity

export interface WeeklyLeaderboardRewardActivity {
    type: 'WEEKLY_LEADERBOARD_REWARD'
    event: {
        id: string
        name: string
    }
    weekStartDate: string
    user: ActivityUser
    position: number
    reward: {
        type: 'case_token' | 'item'
        caseTokens?: number
        item?: TargetItem
    }
    totalEarnedWp: number
    timestamp: string
}

export const useActivityFeedStore = defineStore('activityFeed', {
    state: () => ({
        activities: [] as ActivityItem[],
        isOpen: getInitialFeedOpen(), // Default open as requested. User can toggle it close.
        hasUnread: false,
    }),

    actions: {
        async fetchRecent() {
            try {
                // Fetch slightly more to fill the list
                const response = await getRecentActivity(20)
                // Handle various response types (array, object with data, or object with items)
                let activities = Array.isArray(response)
                    ? response
                    : (response as any).items || (response as any).data || []

                if (Array.isArray(activities)) {
                    this.activities = activities
                } else {
                    console.warn('Recent activity response was not an array:', response)
                }
            } catch (err) {
                console.error('Failed to fetch recent activities:', err)
            }
        },

        addActivity(activity: ActivityItem) {
            // Add to front
            this.activities.unshift(activity)
            // Limit to 50 items
            if (this.activities.length > 50) {
                this.activities.pop()
            }

            if (!this.isOpen) {
                this.hasUnread = true
            }
        },

        toggleFeed() {
            this.isOpen = !this.isOpen
            if (this.isOpen) {
                this.hasUnread = false
            }
        },

        setOpen(value: boolean) {
            this.isOpen = value
            if (value) {
                this.hasUnread = false
            }
        },
    },
})

function getInitialFeedOpen(): boolean {
    if (typeof window === 'undefined') return false
    const stored = window.localStorage.getItem(ACTIVITY_FEED_OPEN_STORAGE_KEY)
    return stored ? stored === '1' : false
}
